import type { GraphOpportunityActionRequest, GraphOpportunityFeedItem } from "@salesko/contracts";
import { BellPlus, ExternalLink, LayoutList, Loader2, Radio, Save, Table2, XCircle } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useI18n } from "../../i18n";
import { ActivityFeedPanel, type ActivityFeedPanelProps } from "./ActivityFeedPanel";
import { DataTable } from "./data/DataTable";
import { useColumnVisibility } from "./data/useColumnVisibility";
import type { DataTableColumnDef } from "./data/tableTypes";
import { compactDateTime } from "../lib/format";
import {
  opportunityActionAlreadyApplied,
  opportunityActionLabel,
  opportunityEvidenceSnippets,
  opportunityFollowUpRequestedAt,
  opportunityHasEvidence,
  opportunityInboxCounts,
  opportunityInsightType,
  opportunityReason,
  opportunitySourceFrameRecordId,
  opportunityStatusLabel,
  opportunitySuggestedNextAction
} from "../lib/opportunityInbox";

type SignalAction = GraphOpportunityActionRequest["action"];
type SignalViewMode = "cards" | "table";

export type SignalsPanelProps = {
  activity?: ActivityFeedPanelProps | null;
  actionError: string | undefined;
  actionPendingId: string | null;
  count: number;
  enabled: boolean;
  error: string | undefined;
  items: GraphOpportunityFeedItem[];
  onAction: (opportunityId: string, action: SignalAction) => void;
  onOpenFrame: (frameRecordId: string) => void;
  pending: boolean;
  readOnly: boolean;
};

type OpportunityInboxPanelProps = Omit<SignalsPanelProps, "activity">;
type CommercialTab = "activity" | "inbox";

type SignalTableRow = {
  evidenceAvailable: boolean;
  item: GraphOpportunityFeedItem;
  score: number;
  sourceFrameRecordId: string | null;
  updatedAtLabel: string;
};

const signalActions = [
  { action: "dismiss", icon: XCircle },
  { action: "save", icon: Save },
  { action: "follow_up", icon: BellPlus }
] satisfies Array<{ action: SignalAction; icon: typeof XCircle }>;

export function SignalsPanel({ activity, ...inboxProps }: SignalsPanelProps) {
  const { t } = useI18n();
  const activityTabId = useId();
  const activityPanelId = useId();
  const inboxTabId = useId();
  const inboxPanelId = useId();
  const activityTabRef = useRef<HTMLButtonElement>(null);
  const inboxTabRef = useRef<HTMLButtonElement>(null);
  const activityEnabled = activity?.enabled === true;
  const [activeTab, setActiveTab] = useState<CommercialTab>(activityEnabled ? "activity" : "inbox");

  useEffect(() => {
    setActiveTab(activityEnabled ? "activity" : "inbox");
  }, [activityEnabled]);

  if (!activityEnabled) {
    return <OpportunityInboxPanel {...inboxProps} />;
  }

  const selectTab = (tab: CommercialTab, moveFocus = false) => {
    setActiveTab(tab);
    if (moveFocus) {
      (tab === "activity" ? activityTabRef : inboxTabRef).current?.focus();
    }
  };
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    let nextTab: CommercialTab | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      nextTab = activeTab === "activity" ? "inbox" : "activity";
    } else if (event.key === "Home") {
      nextTab = "activity";
    } else if (event.key === "End") {
      nextTab = "inbox";
    }
    if (nextTab) {
      event.preventDefault();
      selectTab(nextTab, true);
    }
  };

  return (
    <div className="signals-product-surface">
      <div aria-label={t.activityFeed.tabsAriaLabel} className="signals-product-tabs" role="tablist">
        <button
          aria-controls={activityPanelId}
          aria-selected={activeTab === "activity"}
          className={activeTab === "activity" ? "is-active" : undefined}
          id={activityTabId}
          onClick={() => selectTab("activity")}
          onKeyDown={handleTabKeyDown}
          ref={activityTabRef}
          role="tab"
          tabIndex={activeTab === "activity" ? 0 : -1}
          type="button"
        >
          {t.activityFeed.activityTab}
        </button>
        <button
          aria-controls={inboxPanelId}
          aria-selected={activeTab === "inbox"}
          className={activeTab === "inbox" ? "is-active" : undefined}
          id={inboxTabId}
          onClick={() => selectTab("inbox")}
          onKeyDown={handleTabKeyDown}
          ref={inboxTabRef}
          role="tab"
          tabIndex={activeTab === "inbox" ? 0 : -1}
          type="button"
        >
          {t.activityFeed.inboxTab}
        </button>
      </div>

      {activeTab === "activity" ? (
        <section
          aria-labelledby={activityTabId}
          className="signals-product-panel"
          id={activityPanelId}
          role="tabpanel"
          tabIndex={0}
        >
          <ActivityFeedPanel {...activity} />
        </section>
      ) : (
        <section
          aria-labelledby={inboxTabId}
          className="signals-product-panel"
          id={inboxPanelId}
          role="tabpanel"
          tabIndex={0}
        >
          <OpportunityInboxPanel {...inboxProps} />
        </section>
      )}
    </div>
  );
}

function OpportunityInboxPanel({
  actionError,
  actionPendingId,
  enabled,
  error,
  items,
  onAction,
  onOpenFrame,
  pending,
  readOnly
}: OpportunityInboxPanelProps) {
  const { t } = useI18n();
  const [viewMode, setViewMode] = useState<SignalViewMode>("cards");
  const [columnVisibility, setColumnVisibility] = useColumnVisibility("signals");
  const rows = useMemo(
    () =>
      items.map((item) => ({
        evidenceAvailable: opportunityHasEvidence(item),
        item,
        score: Math.round(item.score * 100),
        sourceFrameRecordId: opportunitySourceFrameRecordId(item),
        updatedAtLabel: compactDateTime(item.updatedAt)
      })),
    [items]
  );
  const signalColumnLabels = useMemo(
    () => ({
      actions: t.signals.columns.actions,
      evidence: t.signals.columns.evidence,
      score: t.signals.columns.score,
      source: t.signals.columns.source,
      status: t.signals.columns.status,
      title: t.signals.columns.title,
      updatedAt: t.signals.columns.updatedAt
    }),
    [t]
  );
  const columns = useMemo<Array<DataTableColumnDef<SignalTableRow>>>(
    () => [
      {
        accessorFn: (row) => row.item.title,
        cell: ({ row }) => (
          <span className="data-table-main">
            <strong>{row.original.item.title}</strong>
            <small>{row.original.item.summary}</small>
          </span>
        ),
        enableHiding: false,
        header: t.signals.columns.title,
        id: "title"
      },
      {
        accessorFn: (row) => row.item.status,
        cell: ({ row }) => (
          <span className={`signal-status signal-status-${row.original.item.status}`}>
            {opportunityStatusLabel(row.original.item.status, t)}
          </span>
        ),
        header: t.signals.columns.status,
        id: "status"
      },
      {
        accessorFn: (row) => row.score,
        cell: ({ row }) => (
          <span aria-label={t.signals.confidence(row.original.score)} className="signal-score" title={t.signals.confidence(row.original.score)}>
            {row.original.score}
          </span>
        ),
        header: t.signals.columns.score,
        id: "score",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => row.item.updatedAt,
        cell: ({ row }) => row.original.updatedAtLabel,
        header: t.signals.columns.updatedAt,
        id: "updatedAt",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => row.evidenceAvailable,
        cell: ({ row }) => row.original.evidenceAvailable ? t.signals.evidenceAvailable : t.signals.evidenceUnavailable,
        header: t.signals.columns.evidence,
        id: "evidence",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => row.sourceFrameRecordId ?? "",
        cell: ({ row }) => row.original.sourceFrameRecordId ? t.signals.sourceAvailable : t.signals.sourceUnavailable,
        header: t.signals.columns.source,
        id: "source"
      },
      {
        cell: ({ row }) => (
          <SignalActionButtons
            actionPending={actionPendingId === row.original.item.id}
            compact
            item={row.original.item}
            onAction={onAction}
            onOpenFrame={onOpenFrame}
            readOnly={readOnly}
            sourceFrameRecordId={row.original.sourceFrameRecordId}
          />
        ),
        enableHiding: false,
        enableSorting: false,
        header: t.signals.columns.actions,
        id: "actions"
      }
    ],
    [actionPendingId, onAction, onOpenFrame, readOnly, t]
  );
  const dispositionCounts = useMemo(() => opportunityInboxCounts(items), [items]);

  return (
    <div className="signals-panel">
      <header className="signals-header">
        <span className="signals-mark" aria-hidden="true">
          <Radio size={20} />
        </span>
        <div>
          <p className="eyebrow">{t.signals.eyebrow}</p>
          <h2>{t.signals.title}</h2>
          <span>
            {enabled
              ? t.signals.inboxSummary(dispositionCounts.pending, dispositionCounts.saved)
              : t.signals.unavailable}
          </span>
        </div>
        {items.length > 0 ? (
          <div className="signals-view-toggle" aria-label={t.signals.viewModeAriaLabel} role="group">
            <button
              aria-pressed={viewMode === "cards"}
              className={viewMode === "cards" ? "is-active" : undefined}
              onClick={() => setViewMode("cards")}
              title={t.signals.cardViewTitle}
              type="button"
            >
              <LayoutList size={15} />
              <span>{t.signals.cards}</span>
            </button>
            <button
              aria-pressed={viewMode === "table"}
              className={viewMode === "table" ? "is-active" : undefined}
              onClick={() => setViewMode("table")}
              title={t.signals.tableViewTitle}
              type="button"
            >
              <Table2 size={15} />
              <span>{t.signals.table}</span>
            </button>
          </div>
        ) : null}
      </header>

      {!enabled ? (
        <p className="signals-empty">{t.signals.signInToLoad}</p>
      ) : pending ? (
        <div className="signals-loading">
          <Loader2 size={18} className="spin" />
          <span>{t.signals.loading}</span>
        </div>
      ) : error ? (
        <p className="signals-error">{error}</p>
      ) : items.length === 0 ? (
        <p className="signals-empty">{t.signals.empty}</p>
      ) : viewMode === "table" ? (
        <DataTable
          className="signals-table"
          columnLabels={signalColumnLabels}
          columnVisibility={columnVisibility}
          columns={columns}
          data={rows}
          emptyLabel={t.signals.empty}
          getRowId={(row) => row.item.id}
          initialSorting={[{ desc: true, id: "score" }]}
          labels={t.dataTable}
          onColumnVisibilityChange={setColumnVisibility}
        />
      ) : (
        <div className="signals-list">
          {items.map((item) => (
            <SignalCard
              actionPending={actionPendingId === item.id}
              item={item}
              key={item.id}
              onAction={onAction}
              onOpenFrame={onOpenFrame}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {actionError ? <p className="signals-error">{actionError}</p> : null}
    </div>
  );
}

function SignalCard({
  actionPending,
  item,
  onAction,
  onOpenFrame,
  readOnly
}: {
  actionPending: boolean;
  item: GraphOpportunityFeedItem;
  onAction: (opportunityId: string, action: SignalAction) => void;
  onOpenFrame: (frameRecordId: string) => void;
  readOnly: boolean;
}) {
  const { t } = useI18n();
  const score = Math.round(item.score * 100);
  const reason = opportunityReason(item);
  const insightType = opportunityInsightType(item);
  const sourceFrameRecordId = opportunitySourceFrameRecordId(item);
  const evidenceSnippets = opportunityEvidenceSnippets(item);
  const suggestedNextAction = opportunitySuggestedNextAction(item);
  const followUpRequestedAt = opportunityFollowUpRequestedAt(item);
  const hasEvidence = opportunityHasEvidence(item);

  return (
    <article className="signal-card">
      <div className="signal-card-top">
        <span className={`signal-status signal-status-${item.status}`}>{opportunityStatusLabel(item.status, t)}</span>
        <span aria-label={t.signals.confidence(score)} className="signal-score" title={t.signals.confidence(score)}>{score}</span>
      </div>
      <h3>{item.title}</h3>
      <p>{item.summary}</p>
      <dl className="signal-meta">
        <div>
          <dt>{t.signals.updated}</dt>
          <dd>{compactDateTime(item.updatedAt)}</dd>
        </div>
        <div>
          <dt>{t.signals.evidence}</dt>
          <dd>{hasEvidence ? t.signals.evidenceAvailable : t.signals.evidenceUnavailable}</dd>
        </div>
        <div>
          <dt>{t.signals.source}</dt>
          <dd>{sourceFrameRecordId ? t.signals.sourceAvailable : t.signals.sourceUnavailable}</dd>
        </div>
      </dl>

      {reason || insightType ? (
        <div className="signal-why">
          <strong>{t.signals.whySurfaced}</strong>
          <span>{reason ?? insightType}</span>
        </div>
      ) : null}

      {followUpRequestedAt ? (
        <p className="signal-action-state">{t.signals.followUpRequested(compactDateTime(followUpRequestedAt))}</p>
      ) : null}

      <details className="signal-detail">
        <summary>{t.signals.details}</summary>
        <div className="signal-detail-body">
          <section>
            <h4>{t.signals.whySurfaced}</h4>
            <p>{reason ?? insightType ?? t.signals.reasonUnavailable}</p>
          </section>
          <section>
            <h4>{t.signals.evidence}</h4>
            {evidenceSnippets.length > 0 ? (
              <ul>
                {evidenceSnippets.map((snippet) => (
                  <li key={snippet}>{snippet}</li>
                ))}
              </ul>
            ) : item.evidenceSpanIds.length > 0 ? (
              <p>{t.signals.evidenceSpanCount(item.evidenceSpanIds.length)}</p>
            ) : (
              <p>{t.signals.evidenceUnavailable}</p>
            )}
          </section>
          {item.entityIds.length > 0 ? (
            <section>
              <h4>{t.signals.relatedEntities}</h4>
              <p>{item.entityIds.join(", ")}</p>
            </section>
          ) : null}
          {sourceFrameRecordId ? (
            <section>
              <h4>{t.signals.relatedFrame}</h4>
              <button className="secondary-button" onClick={() => onOpenFrame(sourceFrameRecordId)} type="button">
                <ExternalLink size={15} />
                <span>{t.signals.openSourceFrame}</span>
              </button>
            </section>
          ) : null}
          {suggestedNextAction ? (
            <section>
              <h4>{t.signals.suggestedNextAction}</h4>
              <p>{suggestedNextAction}</p>
            </section>
          ) : null}
        </div>
      </details>

      <SignalActionButtons
        actionPending={actionPending}
        item={item}
        onAction={onAction}
        onOpenFrame={onOpenFrame}
        readOnly={readOnly}
        sourceFrameRecordId={sourceFrameRecordId}
      />
    </article>
  );
}

function SignalActionButtons({
  actionPending,
  compact = false,
  item,
  onAction,
  onOpenFrame,
  readOnly,
  sourceFrameRecordId
}: {
  actionPending: boolean;
  compact?: boolean;
  item: GraphOpportunityFeedItem;
  onAction: (opportunityId: string, action: SignalAction) => void;
  onOpenFrame: (frameRecordId: string) => void;
  readOnly: boolean;
  sourceFrameRecordId: string | null;
}) {
  const { t } = useI18n();
  const actionDisabled = readOnly || item.status === "dismissed" || actionPending;
  const buttonClassName = compact ? "icon-button" : "secondary-button";
  const textClassName = compact ? "visually-hidden" : undefined;

  return (
    <div className={compact ? "signal-actions signal-actions-compact" : "signal-actions"} aria-label={t.signals.actionsFor(item.title)}>
      {compact && sourceFrameRecordId ? (
        <button
          aria-label={t.signals.openSourceFor(item.title)}
          className={buttonClassName}
          onClick={() => onOpenFrame(sourceFrameRecordId)}
          title={t.signals.openSourceFrame}
          type="button"
        >
          <ExternalLink size={15} />
          <span className={textClassName}>{t.signals.openSourceFrame}</span>
        </button>
      ) : null}
      {signalActions.map((action) => {
        const Icon = actionPending ? Loader2 : action.icon;
        const label = opportunityActionLabel(action.action, item, t);
        return (
          <button
            aria-label={compact ? label : undefined}
            className={buttonClassName}
            disabled={actionDisabled || opportunityActionAlreadyApplied(action.action, item)}
            key={action.action}
            onClick={() => onAction(item.id, action.action)}
            title={label}
            type="button"
          >
            <Icon size={15} className={actionPending ? "spin" : undefined} />
            <span className={textClassName}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
